import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, TrendingUp, Brain, Globe, Shield, Zap, Users } from 'lucide-react';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-2xl">📈</span>
            <span className="font-heading font-bold text-xl">MarketMind</span>
          </Link>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Link>
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">About MarketMind</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            AI-powered stock analysis that helps you make smarter investment decisions by analyzing thousands of news articles in real-time.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-12 px-4 sm:px-6 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-center">Our Mission</h2>
          <p className="text-muted-foreground text-lg text-center max-w-3xl mx-auto">
            We believe everyone deserves access to institutional-quality market analysis. MarketMind democratizes financial intelligence by leveraging cutting-edge AI to analyze news sentiment and predict market movements across multiple time horizons.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-10 text-center">What We Offer</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Brain className="w-6 h-6 text-primary" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-2">AI-Powered Analysis</h3>
                <p className="text-muted-foreground">
                  Our advanced AI models analyze news sentiment, market data, and historical patterns to generate accurate predictions.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-primary" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Global Coverage</h3>
                <p className="text-muted-foreground">
                  Track stocks from markets worldwide with comprehensive news analysis from multiple sources.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-primary" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Multiple Time Horizons</h3>
                <p className="text-muted-foreground">
                  Get predictions for hours, days, weeks, months, and years ahead with confidence scores for each timeframe.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-primary" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Real-Time Updates</h3>
                <p className="text-muted-foreground">
                  Predictions are continuously updated as new information becomes available, keeping you informed.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Transparent Reasoning</h3>
                <p className="text-muted-foreground">
                  Every prediction comes with detailed reasoning that explains the factors driving our analysis.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Community Forum</h3>
                <p className="text-muted-foreground">
                  Join discussions with other investors, share insights, and learn from the community.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 sm:px-6 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-10 text-center">How It Works</h2>
          <div className="space-y-8">
            <div className="flex gap-4 items-start">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">1</div>
              <div>
                <h3 className="font-semibold mb-1">News Collection</h3>
                <p className="text-muted-foreground">We continuously gather news from trusted financial sources including Finnhub, Polygon, and MarketAux.</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">2</div>
              <div>
                <h3 className="font-semibold mb-1">AI Analysis</h3>
                <p className="text-muted-foreground">Our AI models analyze sentiment, identify key factors, and assess the potential impact on stock prices.</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">3</div>
              <div>
                <h3 className="font-semibold mb-1">Prediction Generation</h3>
                <p className="text-muted-foreground">Predictions are generated for multiple time horizons with confidence scores and detailed reasoning.</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">4</div>
              <div>
                <h3 className="font-semibold mb-1">Continuous Updates</h3>
                <p className="text-muted-foreground">Predictions are refreshed regularly as new information emerges, ensuring you have the latest insights.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-muted/50 rounded-lg p-6 border border-border">
            <h3 className="font-semibold mb-3">Important Disclaimer</h3>
            <p className="text-muted-foreground text-sm">
              MarketMind provides AI-generated predictions for informational and educational purposes only. Our analysis should not be construed as financial advice, and we do not recommend buying or selling any securities. Past performance does not guarantee future results. Always conduct your own research and consult with a qualified financial advisor before making investment decisions. Investing involves risk, including the potential loss of principal.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-muted-foreground mb-6">Explore AI-powered stock predictions today.</p>
          <div className="flex gap-4 justify-center">
            <Button asChild>
              <Link to="/stocks">Browse Stocks</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/auth">Create Account</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/40 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-muted-foreground text-sm">
          <p>&copy; {new Date().getFullYear()} MarketMind. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            <Link to="/about" className="hover:text-foreground transition-colors">About</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
